import { DataTable } from "@/components/composables/DataTable";
import { useGetAllTransaction } from "./mutations";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import useControl from "@/hooks/useControl";
import { useState } from "react";
import moment from "moment";
import ViewTaskModal from "./viewTaskModal";
import Pagination from "./pagination";
import { TxContent } from "./types";
import Options from "./transactionOptions";
import { Filter12Filled } from "@fluentui/react-icons";
import { downloadCSV } from "@/utils/helper";
import { Download } from "lucide-react";
import axios from "@/utils/useAxios";
import { toast } from "react-toastify";

const filterFields = [
  { name: "responseCode", label: "Response Code", type: "text" },
  { name: "merchantName", label: "Merchant Name", type: "text" },
  { name: "cardtype", label: "Card Type", type: "text" },
  { name: "paymentType", label: "Payment Type", type: "text" },
  { name: "rrn", label: "RRN", type: "text" },
  { name: "stan", label: "STAN", type: "text" },
  { name: "search", label: "Search", type: "text" },
  { name: "terminalId", label: "Terminal ID", type: "text" },
  { name: "type", label: "Type", type: "text" },
  { name: "merchantId", label: "Merchant ID", type: "text" },
  { name: "transactionId", label: "Transaction ID", type: "text" },
  { name: "status", label: "Status", type: "text" },
  { name: "fromDate", label: "From Date", type: "date" },
  { name: "toDate", label: "To Date", type: "date" },
  { name: "limit", label: "Limit", type: "number" },
];

interface TransactionTableProps {
  filters: { [key: string]: string };
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const TransactionTable = ({ filters, onFilterChange }: TransactionTableProps) => {
  const {
    state: isOpenView,
    setTrue: onOpenView,
    setFalse: onCloseView,
  } = useControl();
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleSidebarClose = () => setSidebarOpen(false);
  const handleSidebarOpen = () => setSidebarOpen(true);

  // Reset to first page when filters change
  const handleFilterChangeWithPageReset = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onFilterChange(e);
    setCurrentPage(1);
  };

  const { data, isPending } = useGetAllTransaction({
    page: currentPage,
    ...filters,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    merchantName: filters.merchantName || undefined,
    cardtype: filters.cardtype || undefined,
    status: filters.status || undefined,
    transactionId: filters.transactionId || undefined,
    terminalId: filters.terminalId || undefined,
    search: filters.search || undefined,
    responseCode: filters.responseCode || undefined,
    paymentType: filters.paymentType || undefined,
    rrn: filters.rrn || undefined,
    stan: filters.stan || undefined,
    type: filters.type || undefined,
    merchantId: filters.merchantId || undefined,
    limit: filters.limit ? Number(filters.limit) : undefined,
  });

  const [, setTransactionId] = useState("");
  const [transaction, setTransaction] = useState<TxContent>();
  const totalPages = data?.paginationInfo?.totalPages as number;

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Function to fetch all transactions for export
  const fetchAllTransactions = async () => {
    try {
      // First, get the total count with current filters
      const countResponse = await axios.get("/admin/transactions/all", {
        params: {
          // Just get 1 record to get pagination info
          page: 1,
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
          merchantName: filters.merchantName || undefined,
          cardtype: filters.cardtype || undefined,
          status: filters.status || undefined,
          transactionId: filters.transactionId || undefined,
          terminalId: filters.terminalId || undefined,
          search: filters.search || undefined,
          responseCode: filters.responseCode || undefined,
          paymentType: filters.paymentType || undefined,
          rrn: filters.rrn || undefined,
          stan: filters.stan || undefined,
          type: filters.type || undefined,
          merchantId: filters.merchantId || undefined,
        },
      });

      const totalCount = countResponse.data.data.paginationInfo?.total || 0;

      if (totalCount === 0) {
        return [];
      }

      // Now fetch all data using the total count as limit
      const response = await axios.get("/admin/transactions/all", {
        params: {
          limit: totalCount, // Use total count to get all filtered data
          fromDate: filters.fromDate || undefined,
          toDate: filters.toDate || undefined,
          merchantName: filters.merchantName || undefined,
          cardtype: filters.cardtype || undefined,
          status: filters.status || undefined,
          transactionId: filters.transactionId || undefined,
          terminalId: filters.terminalId || undefined,
          search: filters.search || undefined,
          responseCode: filters.responseCode || undefined,
          paymentType: filters.paymentType || undefined,
          rrn: filters.rrn || undefined,
          stan: filters.stan || undefined,
          type: filters.type || undefined,
          merchantId: filters.merchantId || undefined,
        },
      });
      return response.data.data.items as TxContent[];
    } catch (error) {
      console.error("Error fetching all transactions:", error);
      toast.error("Failed to fetch all transactions for export");
      return [];
    }
  };

  const handleExportToCSV = async () => {
    setIsExporting(true);

    try {
      // Fetch all transactions with current filters
      const allTransactions = await fetchAllTransactions();

      if (!allTransactions || allTransactions.length === 0) {
        toast.warning("No transactions found to export");
        return;
      }

      // Format the data for CSV export
      const exportData = allTransactions.map((transaction: TxContent) => ({
        "Transaction ID": transaction.transactionId,
        "Terminal ID": transaction.terminalId,
        "Transaction Date": moment(transaction.dateCreated as string).format("YYYY-MM-DD"),
        "Merchant Name": transaction.merchantName || "N/A",
        "Amount": transaction.amount.toFixed(2),
        "Card Type": transaction.cardtype,
        "Status": transaction.status,
        "Response Message": transaction.responsemessage || "N/A",
        "RRN": transaction.rrn || "N/A",
        "STAN": transaction.stan || "N/A",
        "Payment Type": transaction.paymentType || "N/A",
        "Type": transaction.type || "N/A",
        "Merchant ID": transaction.merchantId || "N/A",
        "Response Code": transaction.responseCode || "N/A",
      }));

      // Generate filename with current date and filter info
      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = Object.entries(filters)
        .filter(([_, value]) => value)
        .map(([key, value]) => `${key}-${value}`)
        .join('_');

      const filename = `transactions_${dateStr}${filterStr ? `_${filterStr}` : ''}`;

      downloadCSV(exportData, filename);
      toast.success(`Successfully exported ${allTransactions.length} transactions`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export transactions");
    } finally {
      setIsExporting(false);
    }
  };

  const columns: ColumnDef<TxContent>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "terminalId",
      header: "Terminal ID",
      cell: ({ row }) => <div>{row.original.terminalId}</div>,
    },
    {
      accessorKey: "dateCreated",
      header: "Transaction Date",
      cell: ({ row }) => (
        <div>{moment(row.original.dateCreated as string).format("LL")}</div>
      ),
    },
    {
      header: "Merchant Name",
      cell: ({ row }) => <div>{row.original.merchantName || "N/A"}</div>,
    },
    {
      header: "STAN",
      cell: ({ row }) => <div>{row.original.stan || "N/A"}</div>,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <div>₦{row.original.amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      ),
    },
    {
      accessorKey: "cardtype",
      header: "Card Type",
      cell: ({ row }) => <div>{row.original.cardtype}</div>,
    },
    {
      header: "Status",
      cell: ({ row }) => (
        <div className={`${row.original.status?.toLowerCase() === 'failed'
          ? 'text-red-600'
          : row.original.status?.toLowerCase() === 'approved'
            ? 'text-green-600'
            : 'text-gray-600'
          }`}>
          {row.original.status?.toLowerCase() === 'failed' ? row.original.responsemessage?.toLocaleUpperCase() : row.original.status?.toLocaleUpperCase()}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <Options
          setTransactionId={setTransactionId}
          setTransaction={() => setTransaction(row.original)}
          id={row.original.transactionId}
          onOpenView={onOpenView}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Filter and Export Buttons */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <button
            className="px-4 flex items-center gap-2 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
            onClick={handleSidebarOpen}
          >
            <Filter12Filled className="text-2xl" />
            Filters
          </button>
          <button
            className="px-4 flex items-center gap-2 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
            onClick={handleExportToCSV}
            disabled={isPending || isExporting}
          >
            {isExporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export CSV
              </>
            )}
          </button>
        </div>
        {data?.items && data.items.length > 0 && (
          <div className="text-sm text-gray-600">
            {data.items.length} transaction{data.items.length !== 1 ? 's' : ''} found
          </div>
        )}
      </div>
      {/* RHS Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ maxWidth: 360 }}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Filter Transactions</h2>
          <button
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            onClick={handleSidebarClose}
            aria-label="Close sidebar"
          >
            &times;
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          <form className="space-y-3">
            {filterFields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium mb-1" htmlFor={field.name}>
                  {field.label}
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  value={filters[field.name] || ""}
                  onChange={handleFilterChangeWithPageReset}
                  className="border rounded px-2 py-1 w-full"
                />
              </div>
            ))}
          </form>
        </div>
      </div>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
          onClick={handleSidebarClose}
        />
      )}
      <DataTable
        isLoading={isPending}
        columns={columns}
        data={(data?.items ?? []) as TxContent[]}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
      {isOpenView && (
        <ViewTaskModal
          onClose={onCloseView}
          transaction={transaction as TxContent}
        />
      )}
    </div>
  );
};

export default TransactionTable;
